import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createRoot } from 'react-dom/client';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

import TranslationsProvider, { useTranslation } from '@utils/i18n/i18n';
import translator from '@App/app.translator';
import { useOnClickOutside } from '@services/hooks.service';
import { useMaplibreMap } from '@App/components/MaplibreMap/maplibre-map.context';

import './search-control.css';

const CASE_STUDY_BOUNDS = {
  'catalan-coast': '1.308746,40.853293,3.635101,42.143042',
  'pagasitikos-gulf': '19.30,34.80,26.60,41.75',
};

const CASE_STUDY_COUNTRIES = {
  'catalan-coast': 'es',
  'pagasitikos-gulf': 'gr',
};

const MAPTILER_GEOCODING_URL = 'https://api.maptiler.com/geocoding';

function flyToResult(map, result) {
  const [lng, lat] = result.geometry.coordinates;
  map.flyTo({ center: [lng, lat], zoom: 14 });
}

function GeoSearchBox({ map, caseStudy }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useOnClickOutside(containerRef, () => setSuggestions([]));

  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleClose = () => {
    clearTimeout(debounceRef.current);
    setIsOpen(false);
    setQuery('');
    setSuggestions([]);
  };

  const fetchSuggestions = useCallback(async (q) => {
    if (!q.trim()) {
      setSuggestions([]);
      return;
    }
    setIsSearching(true);
    try {
      const bbox = CASE_STUDY_BOUNDS[caseStudy];
      const country = CASE_STUDY_COUNTRIES[caseStudy];
      const url = `${MAPTILER_GEOCODING_URL}/${encodeURIComponent(q.trim())}.json`;
      const { data } = await axios.get(url, {
        params: {
          bbox,
          country,
          key: import.meta.env.VITE_MAPTILER_KEY,
        },
      });
      setSuggestions(data.features || []);
    } catch {
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, [caseStudy]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
  };

  const handleSearchClick = () => {
    if (suggestions.length > 0) {
      flyToResult(map, suggestions[0]);
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (result) => {
    setQuery(result.place_name);
    setSuggestions([]);
    flyToResult(map, result);
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  if (!isOpen) {
    return (
      <div className="maplibregl-ctrl-group search-toggle-group">
        <button
          type="button"
          className="search-toggle"
          onClick={handleOpen}
          title={t('Search location')}
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </button>
      </div>
    );
  }

  return (
    <div className="search-control-open" ref={containerRef}>
      <div className="maplibregl-ctrl-group search-input-group">
        <button
          type="button"
          className="search-action-btn"
          onClick={handleSearchClick}
          title={t('Search')}
          disabled={suggestions.length === 0 || isSearching}
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </button>
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder={t('Search location...')}
          value={query}
          onChange={handleInputChange}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearchClick(); }}
        />
        <button
          type="button"
          className="search-action-btn"
          onClick={handleClose}
          title={t('Close')}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      {suggestions.length > 0 && (
        <ul className="search-suggestions">
          {suggestions.map((result) => (
            <li key={result.properties.ref}>
              <button
                type="button"
                className="search-suggestion-item"
                onClick={() => handleSuggestionClick(result)}
              >
                {result.place_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

class MaplibreSearchControl {
  constructor(caseStudy) {
    this._caseStudy = caseStudy;
  }

  onAdd(map) {
    this._isRemoved = false;
    this._map = map;
    this._container = document.createElement('div');
    this._container.className = 'maplibregl-ctrl search-control';
    this._root = createRoot(this._container);
    this._root.render(
      <TranslationsProvider translator={translator}>
        <GeoSearchBox map={map} caseStudy={this._caseStudy} />
      </TranslationsProvider>,
    );
    return this._container;
  }

  onRemove() {
    if (this._isRemoved) return;
    this._isRemoved = true;

    const root = this._root;
    const container = this._container;

    this._root = undefined;
    this._container = undefined;
    this._map = undefined;

    if (container?.parentNode) {
      container.parentNode.removeChild(container);
    }

    queueMicrotask(() => root?.unmount());
  }
}

function SearchControl() {
  const { map, isLoading } = useMaplibreMap();
  const { caseStudyId } = useParams();

  useEffect(() => {
    if (!map || isLoading) return undefined;

    if (!CASE_STUDY_BOUNDS[caseStudyId]) return undefined;

    const searchControl = new MaplibreSearchControl(caseStudyId);
    map.addControl(searchControl, 'top-right');

    return () => {
      if (searchControl._map) {
        map.removeControl(searchControl);
      }
    };
  }, [map, isLoading, caseStudyId]);

  return null;
}

export default SearchControl;
